// ============================================================================
// apps/web/src/lib/use-feedback-count.ts
// ----------------------------------------------------------------------------
// Compteur de « retours produits à traiter » (feedback_dish.handled = false) —
// alimente le badge de l'entrée « Retours produits » (sidebar + tiroir + tuile
// accueil + rail kiosk), comme les badges Commandes / Newsletter / Portage.
//
// Source : table public.feedback_dish (même lecture que SupervisionPage, la RLS
// gère l'accès). Réservé aux profils feedback.read.
//
// Rafraîchi : montage, focus / retour d'onglet, toutes les 60 s, et à la demande
// via notifyFeedbackChanged() (appelé par FeedbackPage à chaque bascule traité).
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from './supabase';
import { useAuth } from './auth-context';
import { Capability } from '@magic/shared';

const EVENT = 'magic:feedback-changed';

// À appeler après avoir marqué un retour traité/à traiter pour rafraîchir le badge.
export function notifyFeedbackChanged(): void {
  window.dispatchEvent(new Event(EVENT));
}

export function useNewFeedbackCount(): number {
  const { hasCapability } = useAuth();
  const canSee = hasCapability(Capability.FEEDBACK_READ);
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!canSee) { setCount(0); return; }
    try {
      const { count: c } = await supabase
        .from('feedback_dish')
        .select('id', { count: 'exact', head: true })
        .eq('handled', false);
      setCount(c ?? 0);
    } catch { /* silencieux : le badge n'est pas critique */ }
  }, [canSee]);

  useEffect(() => {
    if (!canSee) { setCount(0); return; }
    void refresh();
    const onFocus = () => { void refresh(); };
    const onVis = () => { if (document.visibilityState === 'visible') void refresh(); };
    const onEvt = () => { void refresh(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener(EVENT, onEvt);
    const id = window.setInterval(() => { void refresh(); }, 60_000);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener(EVENT, onEvt);
      window.clearInterval(id);
    };
  }, [canSee, refresh]);

  return count;
}
